import { IZone, Zone } from "@/models/zone.entity";
import { BoundingBox } from "@/models/bounding-box.entity";
import { ZoneTypeEnum } from "@/lib/enum/zone-type.enum";

// Map each ZoneType to its display colors
// Blue for ZONE (existing), Grey for COMPONENT
export const ZoneTypeColorMap = new Map<ZoneTypeEnum, { fill: string; stroke: string }>([
  [ZoneTypeEnum.ZONE, { fill: "rgba(0, 123, 255, 0.08)", stroke: "rgba(0, 123, 255, 0.9)" }],
  [ZoneTypeEnum.COMPONENT, { fill: "rgba(108, 117, 125, 0.10)", stroke: "rgba(108, 117, 125, 0.9)" }]
]);

export const ZoneTypeIconMap = new Map<ZoneTypeEnum, string>([
  [ZoneTypeEnum.ZONE, "mdi-vector-rectangle"],
  [ZoneTypeEnum.COMPONENT, "mdi-seat"]
]);

// Moved from components/zones/zone-enums.ts
export enum ResizeHandleEnum {
  LEFT = "left",
  RIGHT = "right",
  TOP = "top",
  BOTTOM = "bottom",
  TOP_LEFT = "top-left",
  TOP_RIGHT = "top-right",
  BOTTOM_LEFT = "bottom-left",
  BOTTOM_RIGHT = "bottom-right"
}

export enum AxisEnum {
  X = "x",
  Y = "y"
}

export enum DirectionEnum {
  ABOVE = "above",
  BELOW = "below",
  LEFT = "left",
  RIGHT = "right"
}

export enum ZonesHistoryActionType {
  ADD = "add",
  REMOVE = "remove",
  MOVE = "move",
  JUSTIFY = "justify",
  SAVE = "save",
  CLEAR = "clear",
  RENAME = "rename"
}

class HistorySnapshot {
  name?: ZonesHistoryActionType;
  subjectTitle?: string;
  zones: IZone[];
  editingType?: ZoneTypeEnum;
  fingerprint: string;
  constructor(snapshotState: {
    name?: ZonesHistoryActionType;
    subjectTitle?: string;
    zones: IZone[];
    editingType?: ZoneTypeEnum;
  }) {
    this.name = snapshotState.name;
    this.subjectTitle = snapshotState.subjectTitle;
    this.editingType = snapshotState.editingType;
    this.zones = snapshotState.zones.map(
      z =>
        new Zone({
          name: z.name,
          type: z.type,
          boundingBoxInPercent: {
            x: z.boundingBoxInPercent.x,
            y: z.boundingBoxInPercent.y,
            x2: z.boundingBoxInPercent.x2,
            y2: z.boundingBoxInPercent.y2
          }
        } as IZone)
    );
    this.fingerprint = this.computeFingerprint();
  }
  private computeFingerprint(): string {
    return this.zones
      .map(
        z =>
          `${z.name}|${z.type}|${z.boundingBoxInPercent.x},${z.boundingBoxInPercent.y},${z.boundingBoxInPercent.x2},${z.boundingBoxInPercent.y2}`
      )
      .join(";");
  }
}

class ZonesBase {
  private _zones: IZone[] = [];
  private _past: HistorySnapshot[] = [];
  private _future: HistorySnapshot[] = []; // for redo

  get zones(): IZone[] {
    return this._zones;
  }

  constructor(initial: IZone[] = []) {
    this._zones = initial.map(i => new Zone(i));
    // initial baseline snapshot (no action type)
    const base = new HistorySnapshot({ zones: this._zones });
    this._past.push(base);
  }

  // Return a shallow-cloned array of Zone instances for external consumption
  getAll(): IZone[] {
    return this._zones.map(z => new Zone(z));
  }

  // Expose history stacks for debug UI (read-only snapshots)
  getHistory(): ReadonlyArray<Pick<HistorySnapshot, "name" | "subjectTitle" | "editingType">> {
    return this._past.map(a => ({ name: a.name, subjectTitle: a.subjectTitle, editingType: a.editingType }));
  }

  getFuture(): ReadonlyArray<Pick<HistorySnapshot, "name" | "subjectTitle" | "editingType">> {
    return this._future.map(a => ({ name: a.name, subjectTitle: a.subjectTitle, editingType: a.editingType }));
  }

  private restoreSnapshot(entry: HistorySnapshot) {
    // Replace content of array to preserve reference
    this._zones.splice(0, this._zones.length, ...entry.zones.map(z => new Zone(z)));
  }

  private pushSnapshot(name: ZonesHistoryActionType, subjectTitle: string, currentEditingType: ZoneTypeEnum) {
    const snap = new HistorySnapshot({ name, subjectTitle, zones: this._zones, editingType: currentEditingType });
    // Compare against current tail without explicit index variable usage pattern
    const tailFingerprint = this._past.length ? this._past[this._past.length - 1].fingerprint : undefined;
    if (tailFingerprint && tailFingerprint === snap.fingerprint) return; // skip if no change
    this._past.push(snap);
    this._future = [];
  }

  // Derive next zone name based on a pattern of leading letters followed by digits.
  // Examples: A1 -> A2, AA09 -> AA10, ZONE -> ZONE_1 (fallback if no trailing digits).
  // When duplicating use the original name; when creating use last zone name of same type if pattern matches.
  deriveNextName(base?: string, type?: ZoneTypeEnum): string {
    const list = this._zones.filter(z => !type || z.type === type);
    const pattern = /^([A-Za-z]+)(\d+)$/;
    const source = base || (list.length ? list[list.length - 1].name : "");
    const m = pattern.exec(source);
    if (m) {
      const prefix = m[1];
      const numStr = m[2];
      const nextNum = (parseInt(numStr, 10) + 1).toString().padStart(numStr.length, "0");
      let candidate = `${prefix}${nextNum}`;
      // ensure uniqueness; increment further if collision
      while (this._zones.some(z => z.name === candidate)) {
        const inc = parseInt(candidate.slice(prefix.length), 10) + 1;
        candidate = `${prefix}${inc.toString().padStart(numStr.length, "0")}`;
      }
      return candidate;
    }
    // fallback: append incremental suffix
    const fallbackBase = source || "Z"; // default prefix
    let idx = 1;
    let candidate = `${fallbackBase}${idx}`;
    while (this._zones.some(z => z.name === candidate)) {
      idx++;
      candidate = `${fallbackBase}${idx}`;
    }
    return candidate;
  }

  addZone(zone: IZone): IZone {
    const z = new Zone(zone);
    this._zones.push(z);
    this.pushSnapshot(ZonesHistoryActionType.ADD, z.name, z.type);
    return z;
  }

  // Record a save action for a zone (metadata and/or bounding box). This will merge provided fields
  // into the stored zone and push a SAVE history action that can undo/redo metadata changes.
  saveZone(zone: IZone): IZone {
    const idx = this._zones.findIndex(z => z.name === zone.name);
    if (idx === -1) throw new Error("IZone not found");
    const target = this._zones[idx];

    if (zone.boundingBoxInPercent) {
      const bb = target.boundingBoxInPercent;
      const nb = zone.boundingBoxInPercent;
      bb.x = nb.x;
      bb.y = nb.y;
      bb.x2 = nb.x2;
      bb.y2 = nb.y2;
    }
    this.pushSnapshot(ZonesHistoryActionType.SAVE, target.name, target.type);
    return target;
  }

  // Rename a zone and record history (only name change)
  renameZone(oldName: string, newName: string): IZone {
    const idx = this._zones.findIndex(z => z.name === oldName);
    if (idx === -1) throw new Error("IZone not found");
    const target = this._zones[idx];
    const trimmed = newName.trim();
    if (!trimmed || trimmed === target.name) return target;
    let finalName = trimmed;
    if (this._zones.some(z => z !== target && z.name === finalName)) {
      const base = finalName;
      let i = 1;
      while (this._zones.some(z => z !== target && z.name === `${base}${i}`)) {
        i++;
      }
      finalName = `${base}${i}`;
    }
    const prevName = target.name;
    target.name = finalName;
    this.pushSnapshot(ZonesHistoryActionType.RENAME, `${prevName} -> ${finalName}`, target.type);
    return target;
  }

  // Remove a zone by name (or zone). Returns removed zone.
  removeZone(nameOrZone: string | IZone): IZone {
    const name = typeof nameOrZone === "string" ? nameOrZone : nameOrZone.name;
    const idx = this._zones.findIndex(z => z.name === name);
    if (idx === -1) throw new Error("IZone not found");
    const removed = this._zones.splice(idx, 1)[0];
    this.pushSnapshot(ZonesHistoryActionType.REMOVE, removed?.name, removed.type);
    return removed;
  }

  // Move a zone by name. If newBox is provided, use it; otherwise use the bounding box from the passed zone.
  moveZone(zone: IZone, newBox?: IZone["boundingBoxInPercent"]): IZone {
    const idx = this._zones.findIndex(z => z.name === zone.name);
    if (idx === -1) throw new Error("IZone not found");
    const target = this._zones[idx];
    const boxToSet = newBox ? new BoundingBox(newBox) : new BoundingBox(zone.boundingBoxInPercent);
    boxToSet.justifyBorders();
    const bb = target.boundingBoxInPercent;
    bb.x = boxToSet.x;
    bb.y = boxToSet.y;
    bb.x2 = boxToSet.x2;
    bb.y2 = boxToSet.y2;
    this.pushSnapshot(ZonesHistoryActionType.MOVE, target.name, target.type);
    return target;
  }

  // Lightweight move without recording history (used for live dragging/preview)
  previewMove(zone: IZone, newBox?: IZone["boundingBoxInPercent"]): IZone {
    const idx = this._zones.findIndex(z => z.name === zone.name);
    if (idx === -1) throw new Error("IZone not found");
    const target = this._zones[idx];
    const boxToSet = newBox ? new BoundingBox(newBox) : new BoundingBox(zone.boundingBoxInPercent);
    boxToSet.justifyBorders();
    // mutate in place
    const bb = target.boundingBoxInPercent;
    bb.x = boxToSet.x;
    bb.y = boxToSet.y;
    bb.x2 = boxToSet.x2;
    bb.y2 = boxToSet.y2;
    return target;
  }

  // simple rectangle collision check (percent coords: x,y,x2,y2)
  private collides(a: IZone, b: IZone): boolean {
    // Allow overlap across different types
    if (a.type !== b.type) return false;
    const A = a.boundingBoxInPercent;
    const B = b.boundingBoxInPercent;
    // Treat touching edges (A.x2 === B.x etc.) as non-overlapping => use <=
    return !(A.x2 <= B.x || B.x2 <= A.x || A.y2 <= B.y || B.y2 <= A.y);
  }

  // returns zones that collide with given zone
  getCollisions(subject: IZone): IZone[] {
    return this._zones.filter(z => z.name !== subject.name && this.collides(z, subject));
  }

  // For all zones of the given type return an array of { subject, collisions }
  // where collisions are the zones that collide with the subject
  getCollisionsByType(type: ZoneTypeEnum): { subject: IZone; collisions: IZone[] }[] {
    return this._zones.filter(z => z.type === type).map(z => ({ subject: z, collisions: this.getCollisions(z) }));
  }

  // get neighbors in a direction by proximity; optionally restrict to same type
  getNeighbors(direction: DirectionEnum, subject: IZone, opts?: { type?: ZoneTypeEnum }): IZone[] {
    const subjBox = subject.boundingBoxInPercent;

    // 1) First filter strictly by relative direction using the same logic as justify
    const candidates = this._zones
      .filter(z => z !== subject)
      .filter(z => (opts?.type ? z.type === opts.type : true))
      .filter(z => this.getRelativeDirection(subject, z) === direction);

    // 2) Apply axis-overlap and side checks to ensure geometric validity
    const filtered = candidates.filter(z => {
      const zb = z.boundingBoxInPercent;
      if (direction === DirectionEnum.ABOVE || direction === DirectionEnum.BELOW) {
        const horizOverlap = !(subjBox.x2 < zb.x || zb.x2 < subjBox.x);
        if (!horizOverlap) return false;
        return direction === DirectionEnum.ABOVE ? zb.y2 <= subjBox.y : zb.y >= subjBox.y2;
      }
      // LEFT/RIGHT
      const vertOverlap = !(subjBox.y2 < zb.y || zb.y2 < subjBox.y);
      if (!vertOverlap) return false;
      return direction === DirectionEnum.LEFT ? zb.x2 <= subjBox.x : zb.x >= subjBox.x2;
    });

    if (filtered.length === 0) return [];

    type Cand = { zone: IZone; start: number; end: number; dist: number };

    const cands: Cand[] = filtered.map(z => {
      const zb = z.boundingBoxInPercent;
      if (direction === DirectionEnum.ABOVE) {
        const start = Math.max(zb.x, subjBox.x);
        const end = Math.min(zb.x2, subjBox.x2);
        const dist = subjBox.y - zb.y2; // positive
        return { zone: z, start, end, dist };
      }
      if (direction === DirectionEnum.BELOW) {
        const start = Math.max(zb.x, subjBox.x);
        const end = Math.min(zb.x2, subjBox.x2);
        const dist = zb.y - subjBox.y2;
        return { zone: z, start, end, dist };
      }
      if (direction === DirectionEnum.LEFT) {
        const start = Math.max(zb.y, subjBox.y);
        const end = Math.min(zb.y2, subjBox.y2);
        const dist = subjBox.x - zb.x2;
        return { zone: z, start, end, dist };
      }
      // RIGHT
      const start = Math.max(zb.y, subjBox.y);
      const end = Math.min(zb.y2, subjBox.y2);
      const dist = zb.x - subjBox.x2;
      return { zone: z, start, end, dist };
    });

    // Build partition of the subject face into segments using candidate boundaries
    const coords = new Set<number>();
    if (direction === DirectionEnum.ABOVE || direction === DirectionEnum.BELOW) {
      coords.add(subjBox.x);
      coords.add(subjBox.x2);
      cands.forEach(c => {
        coords.add(c.start);
        coords.add(c.end);
      });
    } else {
      coords.add(subjBox.y);
      coords.add(subjBox.y2);
      cands.forEach(c => {
        coords.add(c.start);
        coords.add(c.end);
      });
    }

    const sorted = Array.from(coords).sort((a, b) => a - b);
    const visible = new Set<IZone>();

    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];
      if (b <= a) continue;
      // find candidates that cover this small segment (a,b)
      const covering = cands.filter(c => c.start < b && c.end > a);
      if (covering.length === 0) continue;
      // pick the closest candidate (smallest dist)
      covering.sort((u, v) => u.dist - v.dist);
      visible.add(covering[0].zone);
    }

    const result = Array.from(visible);

    // compute minimal edge distance for each visible zone (distance along main axis to nearest edge)
    const zoneDist = new Map<IZone, number>();
    result.forEach(z => {
      const entries = cands.filter(c => c.zone === z);
      const min = entries.reduce((m, e) => Math.min(m, e.dist), Number.POSITIVE_INFINITY);
      zoneDist.set(z, min === Number.POSITIVE_INFINITY ? 0 : min);
    });

    // sort by that minimal edge distance (ascending = nearest first)
    result.sort((a, b) => {
      const da = zoneDist.get(a) ?? 0;
      const db = zoneDist.get(b) ?? 0;
      return da - db;
    });

    return result;
  }

  /**
   * Determine the primary relative direction using vector angle between centers of the boxes
   */
  getRelativeDirection(subject: IZone, neighbor: IZone): DirectionEnum {
    const sc = subject.getCenter();
    const nc = neighbor.getCenter();
    const dx = nc.x - sc.x;
    const dy = nc.y - sc.y;

    // atan2 returns radians in [-PI, PI]; convert to degrees [-180, 180]
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

    // Map angle to cardinal directions (screen coords: y increases downward)
    // -45 .. 45 => RIGHT
    // 45 .. 135 => BELOW
    // 135 .. 180 and -180 .. -135 => LEFT
    // -135 .. -45 => ABOVE
    if (angle >= -45 && angle <= 45) {
      return DirectionEnum.RIGHT;
    } else if (angle > 45 && angle <= 135) {
      return DirectionEnum.BELOW;
    } else if (angle > 135 || angle <= -135) {
      return DirectionEnum.LEFT;
    }
    return DirectionEnum.ABOVE;
  }

  // Justify subject with neighbor by moving the subject towards the neighbor along the main axis
  // The direction is inferred from the relative positions of the bounding boxes (percent coords).
  // The subject will be moved so its edge touches the neighbor's adjacent edge (no overlap) along that axis.
  justifyWithNeighbor(subject: IZone, neighbor: IZone): void {
    const sIdx = this._zones.indexOf(subject);
    if (sIdx === -1) throw new Error("Subject zone not found");
    const n = neighbor.boundingBoxInPercent;
    const s = subject.boundingBoxInPercent;
    const direction = this.getRelativeDirection(subject, neighbor);
    switch (direction) {
      case DirectionEnum.LEFT:
        s.x = n.x2;
        break;
      case DirectionEnum.RIGHT:
        s.x2 = n.x;
        break;
      case DirectionEnum.ABOVE:
        s.y = n.y2;
        break;
      case DirectionEnum.BELOW:
        s.y2 = n.y;
        break;
      default:
        break;
    }
    this.pushSnapshot(ZonesHistoryActionType.JUSTIFY, subject.name, subject.type);
  }

  getHitZones(x: number, y: number): IZone[] {
    return this._zones.filter(z => z.isHit(x, y));
  }

  undo(): HistorySnapshot | null {
    if (this._past.length <= 1) return null; // keep baseline
    const target = this._past[this._past.length - 2];
    const popped = this._past.pop();
    if (!target || !popped) return null;
    this._future.push(popped);
    this.restoreSnapshot(target);
    return target;
  }

  redo(): HistorySnapshot | null {
    const next = this._future.pop();
    if (!next) return null;
    this._past.push(next);
    this.restoreSnapshot(next);
    return next;
  }
}

type IZones = ZonesBase;

// Export concrete classes to satisfy both type and value usage in TS/Jest
class Zones extends ZonesBase {}

export { Zones, IZones };
